import React, { useEffect, useState } from 'react'
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Spin,
  Switch,
  Tooltip,
  Typography,
  message
} from 'antd'
import {
  EyeOutlined,
  QuestionCircleOutlined,
  SendOutlined
} from '@ant-design/icons'
import cn from 'classnames'
import dayjs, { Dayjs } from 'dayjs'
import { actionsApi } from 'api/actions'
import {
  ENotificationType,
  ExternalPushMessageData,
  IBroadcastAudience,
  IBroadcastAudienceRequest,
  IBroadcastPreview,
  IBroadcastSegments,
  IBroadcastSellers,
  IBroadcastUsers,
  INotificationCampaign,
  ISendBroadcast,
  InternalPushMessageData
} from 'models/notifications'
import { ISellerNotice } from 'models/sellerNotices'
import { IProductResponse, ISubCategory } from 'models/product'
import { ICollection } from 'models/collections'
import { GetProductModal } from 'components/GetProductModal'
import { GetCollectionModal } from 'components/GetCollectionModal'
import { GetSubCategoryModal } from 'components/GetSubCategoryModal'
import { SellerNoticeModal } from 'components/SellerNoticeModal/SellerNoticeModal'
import { showServerError } from 'shared/utils/showServerError'
import styles from '../Notifications.module.scss'
import { getNotificationTypeName } from './getNotificationTypeName'
import { CampaignProgress } from './CampaignProgress'
import { BroadcastSummary } from './BroadcastSummary'
import { BroadcastPreviewModal, IPreviewState } from './BroadcastPreviewModal'
import { TestBroadcastModal } from './TestBroadcastModal'
import {
  BODY_MAX_LENGTH,
  NO_ORDERS_DAYS_MAX,
  POPUP_BUTTON_MAX_LENGTH,
  SMS_MAX_LENGTH,
  TITLE_MAX_LENGTH,
  addresses,
  chatsCount,
  devices,
  formatDateTime,
  formatRub,
  getLinkLabel,
  getMorningWait,
  getSmsSegments,
  isCampaignActive,
  phonesCount,
  plural,
  sellersCount,
  sellersGenitive,
  smsCount
} from './broadcast'

const AUDIENCE_DEBOUNCE_MS = 400
const PROGRESS_POLL_MS = 1200
const DATE_FORMAT = 'DD.MM.YYYY'
const DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm'

type TLinkMode = 'none' | 'url' | 'app'
type TSendMode = 'now' | 'later'

interface IFormSegments {
  in_cart?: boolean
  registered_after?: Dayjs | null
  no_orders_days?: number | null
}

interface IFormUsers extends Omit<IBroadcastUsers, 'segments'> {
  segments?: IFormSegments
}

interface IFormValues {
  sellersEnabled: boolean
  usersEnabled: boolean
  sellers: IBroadcastSellers
  users: IFormUsers
  title: string
  body: string
  sms_text?: string
  url?: string
  send_mode: TSendMode
  scheduled_at?: Dayjs | null
  quiet_hours: boolean
}

const INITIAL_VALUES: IFormValues = {
  sellersEnabled: false,
  usersEnabled: false,
  sellers: {},
  users: {},
  title: '',
  body: '',
  send_mode: 'now',
  scheduled_at: null,
  quiet_hours: true
}

const APP_LINK_TYPES = [
  ENotificationType.COLLECTION,
  ENotificationType.PRODUCT,
  ENotificationType.FAVORITES,
  ENotificationType.SUBCATEGORY
]

const URL_PATTERN = /^https?:\/\/\S+$/i

const QUIET_HOURS_HINT =
  'Ночь — с 21:00 до 9:00 по местному времени продавца. Часовой пояс берётся по региону ПВЗ СДЭК продавца, как для SMS о новых заказах; без ПВЗ — московское время. Тем, у кого ночь, пуш, бот и SMS придут в 9:00 по их времени, остальным — сразу. Письма, всплывающее окно и пуш покупателям не откладываются.'

const SEGMENTS_HINT =
  'Сужают круг пользователей: сообщение получат те, кто подходит под все отмеченные условия. Заказы учитываются только оплаченные. Устройства без входа в аккаунт проходят по корзине на этом устройстве и по условию «без заказов», а с датой регистрации пуш не получат. Продавцов условия не касаются.'

const toSegments = (
  segments?: IFormSegments
): IBroadcastSegments | undefined => {
  const result: IBroadcastSegments = {}
  if (segments?.in_cart) result.in_cart = true
  if (segments?.registered_after) {
    result.registered_after = segments.registered_after
      .startOf('day')
      .toISOString()
  }
  if (segments?.no_orders_days) {
    result.no_orders_days = segments.no_orders_days
  }
  return Object.keys(result).length ? result : undefined
}

const toRequestUsers = ({
  segments,
  ...users
}: IFormUsers): IBroadcastUsers => ({
  ...users,
  segments: toSegments(segments)
})

const Hint: React.FC<{ title: React.ReactNode }> = ({ title }) => (
  <Tooltip title={title}>
    <QuestionCircleOutlined className={styles.hint} />
  </Tooltip>
)

interface ChannelOptionProps {
  name: [string, string]
  label: string
  hint?: React.ReactNode
  disabled: boolean
  checked?: boolean
  count?: string
  loading: boolean
  children?: React.ReactNode
}

const ChannelOption: React.FC<ChannelOptionProps> = ({
  name,
  label,
  hint,
  disabled,
  checked,
  count,
  loading,
  children
}) => (
  <div className={styles.channel}>
    <div className={styles.channelRow}>
      <Form.Item name={name} valuePropName='checked' noStyle>
        <Checkbox disabled={disabled}>{label}</Checkbox>
      </Form.Item>
      {hint && <Hint title={hint} />}
      {checked && !disabled && (
        <span
          className={cn(styles.channelCount, {
            [styles.channelCountLoading]: loading
          })}
        >
          {count ?? <Spin size='small' />}
        </span>
      )}
    </div>
    {checked && !disabled && children}
  </div>
)

const hasSellerChannel = (sellers?: IBroadcastSellers) =>
  Boolean(
    sellers?.push ||
      sellers?.bot ||
      sellers?.email ||
      sellers?.sms ||
      sellers?.popup
  )

const hasUserChannel = (users?: Pick<IBroadcastUsers, 'push' | 'email'>) =>
  Boolean(users?.push || users?.email)

const getPopupNotice = (
  payload: ISendBroadcast,
  preview: IBroadcastPreview
): Pick<ISellerNotice, 'title' | 'text' | 'link_url' | 'link_text'> => ({
  title: payload.title,
  text: payload.body,
  link_url: preview.site_url,
  link_text: payload.sellers?.popup_button_text?.trim() || preview.link_label
})

export const BroadcastForm: React.FC = () => {
  const [form] = Form.useForm<IFormValues>()
  const [modal, modalContextHolder] = Modal.useModal()

  const [audience, setAudience] = useState<IBroadcastAudience | null>(null)
  const [isAudienceLoading, setIsAudienceLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [campaign, setCampaign] = useState<INotificationCampaign | null>(null)

  const [linkMode, setLinkMode] = useState<TLinkMode>('none')
  const [appLink, setAppLink] = useState<InternalPushMessageData | null>(null)
  const [pickerType, setPickerType] = useState<ENotificationType | null>(null)
  const [popupPreview, setPopupPreview] = useState<Pick<
    ISellerNotice,
    'title' | 'text' | 'link_url' | 'link_text'
  > | null>(null)
  const [preview, setPreview] = useState<IPreviewState | null>(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [testPayload, setTestPayload] = useState<ISendBroadcast | null>(null)

  const sellersEnabled = Form.useWatch('sellersEnabled', form)
  const usersEnabled = Form.useWatch('usersEnabled', form)
  const sellers: IBroadcastSellers =
    Form.useWatch('sellers', { form, preserve: true }) ?? {}
  const users: IFormUsers =
    Form.useWatch('users', { form, preserve: true }) ?? {}
  const smsText: string = Form.useWatch('sms_text', form) ?? ''
  const sendMode: TSendMode = Form.useWatch('send_mode', form) ?? 'now'
  const scheduledAt: Dayjs | null =
    Form.useWatch('scheduled_at', { form, preserve: true }) ?? null

  const activeSellers = sellersEnabled ? sellers : {}
  const activeUsers = usersEnabled ? users : {}
  const hasChannels =
    hasSellerChannel(activeSellers) || hasUserChannel(activeUsers)
  const hasNightChannels = Boolean(
    activeSellers.push || activeSellers.bot || activeSellers.sms
  )
  const hasSegments = Boolean(toSegments(users.segments))
  const isFavoritesLink =
    linkMode === 'app' && appLink?.type === ENotificationType.FAVORITES
  const morningWait = campaign ? getMorningWait(campaign) : null
  const isRunning = isCampaignActive(campaign?.status) && !morningWait
  const scheduledAtIso =
    sendMode === 'later' && scheduledAt ? scheduledAt.toISOString() : undefined

  const audienceRequest: IBroadcastAudienceRequest | null =
    sellersEnabled || usersEnabled
      ? {
          // Текст кнопки на подсчёт не влияет — без него ввод не перезапускает подсчёт
          sellers: sellersEnabled
            ? { ...sellers, popup_button_text: undefined }
            : undefined,
          users: usersEnabled ? toRequestUsers(users) : undefined,
          data: isFavoritesLink
            ? ({ type: ENotificationType.FAVORITES } as InternalPushMessageData)
            : undefined,
          sms_text:
            sellersEnabled && sellers.sms && smsText.trim()
              ? smsText.trim()
              : undefined,
          scheduled_at: scheduledAtIso
        }
      : null
  // Ключ запроса, а не объект: иначе подсчёт перезапускался бы на каждый рендер
  const audienceKey = JSON.stringify(audienceRequest)

  useEffect(() => {
    const request: IBroadcastAudienceRequest | null = JSON.parse(audienceKey)
    if (!request) {
      setAudience(null)
      setIsAudienceLoading(false)
      return
    }

    let cancelled = false
    setIsAudienceLoading(true)
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await actionsApi.getBroadcastAudience(request)
        if (!cancelled) setAudience(response.data)
      } catch (e) {
        if (!cancelled) showServerError(e)
      } finally {
        if (!cancelled) setIsAudienceLoading(false)
      }
    }, AUDIENCE_DEBOUNCE_MS)

    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
    }
  }, [audienceKey])

  useEffect(() => {
    if (!campaign || !isCampaignActive(campaign.status) || morningWait) return

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await actionsApi.getNotificationCampaign(campaign._id)
        const next = response.data
        setCampaign(next)

        if (next.status === 'completed') {
          message.success('Рассылка завершена')
          resetForm()
        }
        if (next.status === 'failed') {
          message.error('Рассылка завершилась с ошибкой — подробности ниже')
        }
        if (next.status === 'canceled') {
          message.warning('Рассылка отменена')
        }
        if (getMorningWait(next)) {
          message.info(
            'Продавцам, у которых ночь, сообщения уйдут в 9:00 по их времени — ход рассылки виден в «Истории»'
          )
          resetForm()
        }
      } catch {
        message.error('Не удалось получить статус рассылки')
      }
    }, PROGRESS_POLL_MS)

    return () => window.clearTimeout(timeoutId)
  }, [campaign])

  const resetForm = () => {
    form.resetFields()
    setLinkMode('none')
    setAppLink(null)
  }

  const toggleGroup = (group: 'sellers' | 'users', enabled: boolean) => {
    if (!enabled) form.setFieldValue(group, {})
  }

  const changeLinkMode = (mode: TLinkMode) => {
    setLinkMode(mode)
    setAppLink(null)
    form.setFieldValue('url', undefined)
  }

  const changeAppLinkType = (type: ENotificationType) => {
    setAppLink({
      type,
      typeId: null,
      typeSlug: null
    } as unknown as InternalPushMessageData)
    if (type !== ENotificationType.FAVORITES) setPickerType(type)
  }

  const pickProduct = (products: IProductResponse[]) => {
    const product = products[0]
    if (!product) return
    setAppLink({
      type: ENotificationType.PRODUCT,
      typeId: product._id,
      typeSlug: product.slug
    })
  }

  const pickCollection = (collections: ICollection[]) => {
    const collection = collections[0]
    if (!collection) return
    setAppLink({
      type: ENotificationType.COLLECTION,
      typeId: collection._id,
      typeSlug: `${collection.slug}-${collection._id}`
    })
  }

  const pickSubCategory = (subCategory: ISubCategory) => {
    setAppLink({
      type: ENotificationType.SUBCATEGORY,
      typeId: subCategory._id,
      typeSlug: subCategory.slug
    })
  }

  const getLink = (
    values: IFormValues
  ): InternalPushMessageData | ExternalPushMessageData | undefined => {
    if (linkMode === 'url' && values.url) return { url: values.url.trim() }
    if (linkMode === 'app' && appLink) {
      return appLink.type === ENotificationType.FAVORITES
        ? ({ type: appLink.type } as InternalPushMessageData)
        : appLink
    }
    return undefined
  }

  const isAppLinkIncomplete =
    linkMode === 'app' &&
    (!appLink ||
      (appLink.type !== ENotificationType.FAVORITES && !appLink.typeId))

  // Из всего хранилища формы: поля скрытых блоков onFinish не отдаёт
  const buildPayload = (): ISendBroadcast => {
    const values: IFormValues = form.getFieldsValue(true)
    const data = getLink(values)
    return {
      title: values.title?.trim() ?? '',
      body: values.body?.trim() ?? '',
      sellers: values.sellersEnabled
        ? {
            ...values.sellers,
            popup_button_text: data
              ? values.sellers?.popup_button_text?.trim() || undefined
              : undefined
          }
        : undefined,
      users: values.usersEnabled
        ? toRequestUsers(values.users ?? {})
        : undefined,
      sms_text:
        values.sellersEnabled && values.sellers?.sms
          ? values.sms_text?.trim()
          : undefined,
      data,
      quiet_hours: values.quiet_hours,
      scheduled_at:
        values.send_mode === 'later' && values.scheduled_at
          ? values.scheduled_at.toISOString()
          : undefined
    }
  }

  const getValidatedPayload = async (fields: string[]) => {
    try {
      await form.validateFields(fields)
    } catch {
      return null
    }
    if (isAppLinkIncomplete) {
      message.error('Выберите, куда ведёт ссылка')
      return null
    }
    return buildPayload()
  }

  const handleSubmit = async () => {
    if (!hasChannels) {
      message.error('Выберите хотя бы один канал отправки')
      return
    }
    if (isAppLinkIncomplete) {
      message.error('Выберите, куда ведёт ссылка')
      return
    }

    const payload = buildPayload()
    setIsSending(true)
    let actual: IBroadcastAudience
    try {
      // Свежий подсчёт: в подтверждении цифры должны совпадать с тем, что уйдёт
      const response = await actionsApi.getBroadcastAudience(
        JSON.parse(audienceKey)
      )
      actual = response.data
      setAudience(actual)
    } catch (e) {
      showServerError(e)
      setIsSending(false)
      return
    }
    setIsSending(false)

    const confirmed = await modal.confirm({
      title: payload.scheduled_at
        ? 'Запланировать рассылку?'
        : 'Отправить рассылку?',
      width: 560,
      content: (
        <BroadcastSummary
          audience={actual}
          sellers={payload.sellers ?? {}}
          users={payload.users ?? {}}
          smsText={payload.sms_text}
          linkLabel={getLinkLabel(payload.data)}
          scheduledAt={payload.scheduled_at}
          quietHours={payload.quiet_hours}
        />
      ),
      okText: payload.scheduled_at ? 'Запланировать' : 'Отправить',
      cancelText: 'Отмена'
    })
    if (!confirmed) return

    setIsSending(true)
    try {
      const response = await actionsApi.sendBroadcast(payload)
      const next = response.data
      setCampaign(next)
      if (next.status === 'scheduled') {
        message.success(
          `Рассылка запланирована на ${formatDateTime(
            next.scheduled_at ?? payload.scheduled_at!
          )}. Отменить можно в «Истории»`
        )
        resetForm()
      } else {
        message.success('Рассылка запущена')
      }
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSending(false)
    }
  }

  const loadPreview = async () => {
    const payload = await getValidatedPayload(['title', 'body', 'url'])
    if (!payload) return null
    setIsPreviewLoading(true)
    try {
      const response = await actionsApi.previewBroadcast(payload)
      return { payload, preview: response.data }
    } catch (e) {
      showServerError(e)
      return null
    } finally {
      setIsPreviewLoading(false)
    }
  }

  const openPreview = async () => {
    const next = await loadPreview()
    if (next) setPreview(next)
  }

  const showPopupPreview = async () => {
    const next = await loadPreview()
    if (next) setPopupPreview(getPopupNotice(next.payload, next.preview))
  }

  const openTest = async () => {
    if (!hasChannels) {
      message.error('Выберите хотя бы один канал отправки')
      return
    }
    const payload = await getValidatedPayload([
      'title',
      'body',
      'sms_text',
      'url'
    ])
    if (payload) setTestPayload(payload)
  }

  const count = (format: (value: number) => string, value?: number) =>
    audience && value !== undefined ? format(value) : undefined

  const smsSegments = getSmsSegments(smsText.trim())
  const smsCost = audience?.sms_cost ?? null
  const smsBalance = audience?.sms_balance ?? null
  const isSmsBalanceShort =
    smsCost !== null && smsBalance !== null && smsCost.total_cost > smsBalance
  const smsInfo = [
    smsSegments > 0 && `${smsCount(smsSegments)} на номер`,
    smsCost &&
      `всего ${smsCount(smsCost.total_sms)} ≈ ${formatRub(smsCost.total_cost)}`,
    smsBalance !== null
      ? `баланс sms.ru ${formatRub(smsBalance)}`
      : 'баланс sms.ru не получен'
  ]
    .filter(Boolean)
    .join(' · ')
  const nightCount = audience?.sellers.night ?? null
  const nightMoment = scheduledAtIso ? 'в это время' : 'сейчас'
  const showSharedTotals =
    (activeSellers.push && activeUsers.push) ||
    (activeSellers.email && activeUsers.email)

  return (
    <Form
      form={form}
      layout='vertical'
      initialValues={INITIAL_VALUES}
      onFinish={handleSubmit}
      className={styles.form}
    >
      {modalContextHolder}

      <Row gutter={[24, 16]}>
        <Col xs={24} lg={12}>
          <div
            className={cn(styles.group, {
              [styles.groupActive]: sellersEnabled
            })}
          >
            <div className={styles.groupHead}>
              <Form.Item name='sellersEnabled' valuePropName='checked' noStyle>
                <Switch onChange={(on) => toggleGroup('sellers', on)} />
              </Form.Item>
              <span className={styles.groupTitle}>Всем продавцам</span>
              {sellersEnabled && audience && (
                <span className={styles.groupMeta}>
                  {plural(audience.sellers.total, [
                    'одобренный',
                    'одобренных',
                    'одобренных'
                  ])}
                </span>
              )}
            </div>
            <ChannelOption
              name={['sellers', 'push']}
              label='Push-уведомление'
              hint='Устройства, где в приложении выполнен вход в аккаунт продавца'
              disabled={!sellersEnabled}
              checked={sellers.push}
              count={count(devices, audience?.sellers.push)}
              loading={isAudienceLoading}
            />
            <ChannelOption
              name={['sellers', 'bot']}
              label='Seller-бот (TG / MAX)'
              hint={
                audience
                  ? `Продавцы, подключившие бота в кабинете. Сообщение уходит в каждый подключённый мессенджер: Telegram — ${chatsCount(
                      audience.sellers.bot_telegram
                    )}, MAX — ${chatsCount(audience.sellers.bot_max)}.`
                  : 'Продавцы, подключившие бота в кабинете'
              }
              disabled={!sellersEnabled}
              checked={sellers.bot}
              count={count(sellersCount, audience?.sellers.bot)}
              loading={isAudienceLoading}
            />
            <ChannelOption
              name={['sellers', 'email']}
              label='E-mail рассылка'
              hint='В письме есть ссылка «Отписаться от рассылки» — отписавшимся письма больше не приходят'
              disabled={!sellersEnabled}
              checked={sellers.email}
              count={count(addresses, audience?.sellers.email)}
              loading={isAudienceLoading}
            />
            <ChannelOption
              name={['sellers', 'sms']}
              label='SMS-рассылка'
              hint='Платная. Текст SMS задаётся отдельно, ссылки-кнопки и заголовка в SMS нет. Стоимость и баланс sms.ru видны под текстом SMS.'
              disabled={!sellersEnabled}
              checked={sellers.sms}
              count={count(phonesCount, audience?.sellers.sms)}
              loading={isAudienceLoading}
            />
            <ChannelOption
              name={['sellers', 'popup']}
              label='Всплывающее окно'
              hint='Окно в кабинете продавца: показывается при каждом входе, пока продавец не отметит «Не показывать снова». Изменить или выключить — на вкладке «Всплывающие окна».'
              disabled={!sellersEnabled}
              checked={sellers.popup}
              count={count(sellersCount, audience?.sellers.popup)}
              loading={isAudienceLoading}
            >
              <div className={styles.channelNested}>
                <Form.Item
                  name={['sellers', 'popup_notify_new_sellers']}
                  valuePropName='checked'
                  noStyle
                >
                  <Checkbox>Уведомлять новых селлеров</Checkbox>
                </Form.Item>
                <Hint title='Без галочки окно увидят только продавцы, зарегистрированные до отправки. С галочкой — и те, кто зарегистрируется позже.' />
                <Button
                  type='link'
                  size='small'
                  icon={<EyeOutlined />}
                  loading={isPreviewLoading}
                  onClick={showPopupPreview}
                >
                  Как увидит продавец
                </Button>
              </div>
              {linkMode !== 'none' && (
                <div className={styles.channelNested}>
                  <span>Текст кнопки</span>
                  <Form.Item name={['sellers', 'popup_button_text']} noStyle>
                    <Input
                      size='small'
                      maxLength={POPUP_BUTTON_MAX_LENGTH}
                      placeholder='По типу ссылки'
                      className={styles.popupButtonInput}
                    />
                  </Form.Item>
                  <Hint title='Кнопка в окне открывает ссылку на сайт в новой вкладке. Пустое поле — подпись по типу ссылки: «Открыть товар», «Открыть подборку», для внешней ссылки — «Подробнее». Точная подпись — в предпросмотре.' />
                </div>
              )}
            </ChannelOption>
          </div>
        </Col>

        <Col xs={24} lg={12}>
          <div
            className={cn(styles.group, { [styles.groupActive]: usersEnabled })}
          >
            <div className={styles.groupHead}>
              <Form.Item name='usersEnabled' valuePropName='checked' noStyle>
                <Switch onChange={(on) => toggleGroup('users', on)} />
              </Form.Item>
              <span className={styles.groupTitle}>
                {hasSegments && usersEnabled
                  ? 'Пользователям по условиям'
                  : 'Всем пользователям'}
              </span>
            </div>
            <ChannelOption
              name={['users', 'push']}
              label='Push-уведомление'
              hint={
                users.marketing_only
                  ? 'Устройства пользователей, давших согласие на рассылку'
                  : 'Все устройства с приложением, в том числе без входа в аккаунт'
              }
              disabled={!usersEnabled}
              checked={users.push}
              count={count(devices, audience?.users.push)}
              loading={isAudienceLoading}
            />
            <ChannelOption
              name={['users', 'email']}
              label='E-mail рассылка'
              hint='Только подтверждённые адреса. Отписавшиеся по ссылке из письма не получат.'
              disabled={!usersEnabled}
              checked={users.email}
              count={count(addresses, audience?.users.email)}
              loading={isAudienceLoading}
            />
            <div className={cn(styles.channel, styles.channelConsent)}>
              <div className={styles.channelRow}>
                <Form.Item
                  name={['users', 'marketing_only']}
                  valuePropName='checked'
                  noStyle
                >
                  <Checkbox disabled={!usersEnabled}>
                    Только давшие согласие на рассылку
                  </Checkbox>
                </Form.Item>
                <Hint title='Пользователи, согласившиеся получать рекламные рассылки. Рекламу без согласия закон запрещает (ст. 18 38-ФЗ). Устройства без входа в аккаунт пуш при этом не получат. Продавцов галочка не касается.' />
              </div>
            </div>
            <div className={cn(styles.channel, styles.segments)}>
              <div className={styles.channelRow}>
                <Typography.Text
                  type={usersEnabled ? undefined : 'secondary'}
                  strong
                >
                  Условия
                </Typography.Text>
                <Hint title={SEGMENTS_HINT} />
              </div>
              <div className={styles.segmentRow}>
                <Form.Item
                  name={['users', 'segments', 'in_cart']}
                  valuePropName='checked'
                  noStyle
                >
                  <Checkbox disabled={!usersEnabled}>
                    С товарами в корзине
                  </Checkbox>
                </Form.Item>
              </div>
              <div className={styles.segmentRow}>
                <span>Зарегистрированы с</span>
                <Form.Item
                  name={['users', 'segments', 'registered_after']}
                  noStyle
                >
                  <DatePicker
                    size='small'
                    format={DATE_FORMAT}
                    placeholder='Любая дата'
                    disabled={!usersEnabled}
                    disabledDate={(date) => date.isAfter(dayjs(), 'day')}
                  />
                </Form.Item>
              </div>
              <div className={styles.segmentRow}>
                <span>Без оплаченных заказов</span>
                <Form.Item
                  name={['users', 'segments', 'no_orders_days']}
                  noStyle
                >
                  <InputNumber
                    size='small'
                    min={1}
                    max={NO_ORDERS_DAYS_MAX}
                    precision={0}
                    placeholder='—'
                    disabled={!usersEnabled}
                    className={styles.segmentDays}
                  />
                </Form.Item>
                <span>дней</span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {showSharedTotals && audience && (
        <Typography.Paragraph type='secondary' className={styles.sharedTotals}>
          Устройства и адреса, общие для продавцов и пользователей, получат
          сообщение один раз. Всего:{' '}
          {[
            (activeSellers.push || activeUsers.push) &&
              `пушей — ${audience.total.push.toLocaleString('ru-RU')}`,
            (activeSellers.email || activeUsers.email) &&
              `писем — ${audience.total.email.toLocaleString('ru-RU')}`
          ]
            .filter(Boolean)
            .join(', ')}
          .
        </Typography.Paragraph>
      )}

      <Typography.Title level={4} className={styles.sectionTitle}>
        Сообщение
      </Typography.Title>

      <Form.Item
        name='title'
        label={
          <span className={styles.labelWithHint}>
            Заголовок
            <Hint title='Заголовок пуша и окна, тема письма, первая строка в боте' />
          </span>
        }
        rules={[
          { required: true, whitespace: true, message: 'Заполните заголовок' }
        ]}
      >
        <Input maxLength={TITLE_MAX_LENGTH} showCount size='large' />
      </Form.Item>
      <Form.Item
        name='body'
        label='Текст'
        rules={[
          { required: true, whitespace: true, message: 'Заполните текст' }
        ]}
      >
        <Input.TextArea
          maxLength={BODY_MAX_LENGTH}
          showCount
          autoSize={{ minRows: 4, maxRows: 14 }}
        />
      </Form.Item>

      {sellersEnabled && sellers.sms && (
        <Form.Item
          name='sms_text'
          label={
            <span className={styles.labelWithHint}>
              Текст SMS
              <Hint title='Каждые 70 символов кириллицей — отдельная платная SMS, в длинном сообщении — по 67. Латиницей — 160 и 153. Стоимость считает sms.ru по номерам получателей.' />
            </span>
          }
          rules={[
            { required: true, whitespace: true, message: 'Заполните текст SMS' }
          ]}
          extra={
            <span className={cn({ [styles.smsShort]: isSmsBalanceShort })}>
              {smsInfo}
              {isSmsBalanceShort && ' — баланса не хватит на все SMS'}
            </span>
          }
        >
          <Input.TextArea
            maxLength={SMS_MAX_LENGTH}
            showCount
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
        </Form.Item>
      )}

      <div className={styles.linkBlock}>
        <span className={styles.labelWithHint}>
          Ссылка
          <Hint title='Внешняя ссылка — любой адрес. Страница HorseSmart — товар, подборка, подкатегория или избранное: из пуша откроется экран приложения, в письме, боте и всплывающем окне — страница сайта. В SMS ссылок нет.' />
        </span>
        <Radio.Group
          value={linkMode}
          onChange={(e) => changeLinkMode(e.target.value)}
          options={[
            { value: 'none', label: 'Без ссылки' },
            { value: 'url', label: 'Внешняя ссылка' },
            { value: 'app', label: 'Страница HorseSmart' }
          ]}
        />
        {linkMode === 'url' && (
          <Form.Item
            name='url'
            className={styles.linkInput}
            rules={[
              { required: true, message: 'Укажите ссылку' },
              {
                pattern: URL_PATTERN,
                message: 'Ссылка должна начинаться с http:// или https://'
              }
            ]}
          >
            <Input placeholder='https://horsesmart.store/...' size='large' />
          </Form.Item>
        )}
        {linkMode === 'app' && (
          <div className={styles.appLink}>
            <Select
              placeholder='Куда ведёт'
              value={appLink?.type}
              onChange={changeAppLinkType}
              style={{ width: 200 }}
              options={APP_LINK_TYPES.map((type) => ({
                value: type,
                label: getNotificationTypeName(type)
              }))}
            />
            {appLink && appLink.type !== ENotificationType.FAVORITES && (
              <>
                <Button onClick={() => setPickerType(appLink.type)}>
                  {appLink.typeId ? 'Изменить' : 'Выбрать'}
                </Button>
                <Typography.Text
                  type={appLink.typeId ? 'success' : 'danger'}
                  className={styles.appLinkPath}
                >
                  {appLink.typeSlug || 'Не выбрано'}
                </Typography.Text>
              </>
            )}
          </div>
        )}
        {isFavoritesLink && (
          <Typography.Text type='secondary'>
            Пуш получат только устройства, где есть товары в избранном.
          </Typography.Text>
        )}
      </div>

      <Typography.Title level={4} className={styles.sectionTitle}>
        Когда
      </Typography.Title>

      <div className={styles.whenBlock}>
        <div className={styles.channelRow}>
          <Form.Item name='send_mode' noStyle>
            <Radio.Group
              options={[
                { value: 'now', label: 'Сразу' },
                { value: 'later', label: 'Запланировать' }
              ]}
            />
          </Form.Item>
          <Hint title='Запланированную рассылку можно отменить в «Истории» до её начала. Получатели считаются в момент отправки: кто зарегистрируется до неё, тоже получит.' />
        </div>
        {sendMode === 'later' && (
          <Form.Item
            name='scheduled_at'
            className={styles.whenPicker}
            rules={[
              { required: true, message: 'Укажите дату и время отправки' },
              {
                validator: (_, value?: Dayjs | null) =>
                  !value || value.isAfter(dayjs())
                    ? Promise.resolve()
                    : Promise.reject(new Error('Это время уже прошло'))
              }
            ]}
          >
            <DatePicker
              showTime={{ format: 'HH:mm', minuteStep: 5 }}
              format={DATE_TIME_FORMAT}
              placeholder='Дата и время'
              size='large'
              disabledDate={(date) => date.isBefore(dayjs(), 'day')}
            />
          </Form.Item>
        )}
        {hasNightChannels && (
          <div className={styles.channelRow}>
            <Form.Item name='quiet_hours' valuePropName='checked' noStyle>
              <Checkbox>Не беспокоить продавцов ночью</Checkbox>
            </Form.Item>
            <Hint title={QUIET_HOURS_HINT} />
            {nightCount !== null && (
              <span className={styles.nightCount}>
                {nightCount
                  ? `${nightMoment} ночь у ${sellersGenitive(nightCount)}`
                  : `${nightMoment} ни у кого не ночь`}
              </span>
            )}
          </div>
        )}
      </div>

      {campaign && <CampaignProgress campaign={campaign} />}

      <div className={styles.formActions}>
        <Button
          type='primary'
          htmlType='submit'
          size='large'
          disabled={!hasChannels || isRunning}
          loading={isSending || isRunning}
        >
          {isRunning
            ? 'Рассылка идёт...'
            : sendMode === 'later'
            ? 'Запланировать'
            : 'Отправить'}
        </Button>
        <Button
          size='large'
          icon={<EyeOutlined />}
          loading={isPreviewLoading}
          onClick={openPreview}
        >
          Предпросмотр
        </Button>
        <Button
          size='large'
          icon={<SendOutlined />}
          disabled={!hasChannels}
          onClick={openTest}
        >
          Отправить себе
        </Button>
        <Hint title='Тест только вам: пуш — на ваши устройства, бот — в бот модерации, письмо — на info@horsesmart.store, SMS — на номер, который укажете. Окно себе не отправляется — оно есть в предпросмотре.' />
      </div>

      <GetProductModal
        multiple={false}
        onSave={pickProduct}
        visible={pickerType === ENotificationType.PRODUCT}
        onClose={() => setPickerType(null)}
      />
      <GetCollectionModal
        multiple={false}
        onSave={pickCollection}
        visible={pickerType === ENotificationType.COLLECTION}
        onClose={() => setPickerType(null)}
      />
      <GetSubCategoryModal
        multiple={false}
        onSave={pickSubCategory}
        visible={pickerType === ENotificationType.SUBCATEGORY}
        onClose={() => setPickerType(null)}
      />
      <BroadcastPreviewModal
        state={preview}
        onClose={() => setPreview(null)}
        onShowPopup={() =>
          preview &&
          setPopupPreview(getPopupNotice(preview.payload, preview.preview))
        }
      />
      <TestBroadcastModal
        payload={testPayload}
        onClose={() => setTestPayload(null)}
      />
      <SellerNoticeModal
        notice={popupPreview}
        onClose={() => setPopupPreview(null)}
      />
    </Form>
  )
}
